"use client";

import { GE } from ".constants/gtmEvents";
import { usePortalContext } from "@cloai/uikit";
import { SyntheticEvent, useCallback, useEffect, useState } from "react";

import { Gender } from "@/.gql/graphql";
import { useAiRequestInfo } from "@/components/hooks/useAiRequestInfo";
import { useClickUndress } from "@/components/user-flow/controls/useClickUndress";
import { PhotoSettings, useGTMContext, useUndressContext } from "@/providers";
import { generateUUID } from "@/utils/crypto";

export const useAnimate = (token: string | null) => {
  const { closePortal } = usePortalContext();
  const {
    initialSettings,
    clickUndress,
    clickUndressingFile,
    undressingsFiles,
    onReset,
    setLoading,
    setSettings,
    setClickFileId,
    setUndressingsFiles,
  } = useUndressContext();
  const { sendGTM } = useGTMContext();

  const { onSignUndressAnimate } = useClickUndress(token);

  // Находим категорию для `aiRequest` по его `id`
  const { findCategoryById } = useAiRequestInfo();

  const [isReadyToAnimate, setIsReadyToAnimate] = useState(false);

  const handleAnimate = useCallback(
    async (event: SyntheticEvent) => {
      try {
        const url = clickUndressingFile?.fileUrl;

        if (!url) {
          throw new Error("No file URL");
        }

        // Сохраняем настройки прошлой генерации перед сбросом
        const aiRequestIds = clickUndress?.aiRequestIds || [];

        // Сбрасываем текущую генерацию
        await onReset(event, true);

        const newFile = { id: generateUUID(), fileUrl: url };

        // Обновляем состояние с массивом изображений
        setUndressingsFiles([newFile]);

        // Обновляем настройки для нового изображения

        // Определяем соответствие категорий `AiRequests` и полей `PhotoSettings`
        const categoryToFieldMap: { [key: string]: keyof PhotoSettings } = {
          busts: "breastSize",
          bodys: "bodyType",
          qualities: "quality",
          ages: "age",
          costumes: "costume",
          butts: "buttSize",
          poses: "pose",
          styles: "style",
          environments: "environment",
          sets: "set",
          storylines: "storyline",
          hairColors: "hairColor",
          skinColors: "skinColor",
        };

        // Функция для преобразования строки в `Gender`
        const toGender = (value: string): Gender => {
          const strValue = String(value).toLowerCase();
          if (strValue === "female") {
            return Gender.Famale;
          }
          if (strValue === "male") {
            return Gender.Male;
          }
          return Gender.Famale; // Дефолтное значение
        };

        // Создаём новый объект настроек
        const newSettings: PhotoSettings = {
          ...initialSettings,
          id: newFile.id,
        };

        // Обновляем поля на основе `aiRequestIds`
        aiRequestIds.forEach((id) => {
          const category = findCategoryById(id);
          if (category && categoryToFieldMap[category]) {
            if (categoryToFieldMap[category] === "gender") {
              newSettings[categoryToFieldMap[category]] = toGender(id);
            } else {
              newSettings[categoryToFieldMap[category]] = id;
            }
          }
        });

        setSettings([newSettings]);

        // Выбираем добавленное изображение в качестве активного
        setClickFileId(newFile.id);

        sendGTM({ event: GE.ANIMATE_POPUP__CLICK_ANIMATE });

        // Делаем паузу, чтобы React успел обновить состояние

        // TODO: делаем запрос на генку анимации
        // TODO: ПОЧИНИТЬ
        // await onSignUndressAnimate();

        // Устанавливаем флаг готовности к анимации
        setIsReadyToAnimate(true);

        closePortal();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [
      clickUndress?.aiRequestIds,
      clickUndressingFile?.fileUrl,
      closePortal,
      findCategoryById,
      initialSettings,
      onReset,
      sendGTM,
      setClickFileId,
      setLoading,
      setSettings,
      setUndressingsFiles,
    ],
  );

  // Отслеживаем изменение состояния и наличие файлов
  useEffect(() => {
    if (isReadyToAnimate && undressingsFiles.length > 0) {
      onSignUndressAnimate();
      setIsReadyToAnimate(false); // Сбрасываем флаг
    }
  }, [isReadyToAnimate, undressingsFiles, onSignUndressAnimate]);

  return handleAnimate;
};
